import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Subscription, firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

interface BonusRequest {
  id: string;
  userId: string;
  userEmail: string;
  hubspotId?: string;
  amount: number;
  percentage: number;
  projectName: string;
  projectId?: string; // ID del proyecto asociado
  paymentMethod: string;
  status: 'CONFIGURED' | 'PENDING' | 'APPLIED' | 'CANCELLED';
  requestStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  configuredAt: number;
  appliedAt: string | null;
  requestId?: string;
  rejectionReason?: string;
  reviewedAt?: number;
  reviewedBy?: string;
  bonusNumber: number;
  // Nuevas propiedades para aprobaciones selectivas
  approvalType?: 'amount' | 'percentage' | 'both';
  approvedAmount?: number;
  approvedPercentage?: number;
  appliedAmount?: number;
  appliedPercentage?: number;
}

interface Project {
  id: string;
  nameTokens?: string;
  title?: string;
  location?: string;
  status?: string;
  proyectStatus?: string;
}

@Component({
  selector: 'app-manage-bonuses',
  templateUrl: './manage-bonuses.component.html',
  styleUrls: ['./manage-bonuses.component.css']
})
export class ManageBonusesComponent implements OnInit, OnDestroy {
  bonusRequests: BonusRequest[] = [];
  availableProjects: Project[] = [];
  isLoading: boolean = false;
  
  // Estados para fold/unfold de cada sección
  isPendingExpanded: boolean = true;
  isApprovedExpanded: boolean = true;
  isRejectedExpanded: boolean = true;
  
  private subscriptions: Subscription[] = [];
  private hubspotIdCache: Map<string, string> = new Map(); // Cache de HubSpot IDs

  constructor(
    private afs: AngularFirestore,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadBonusRequests();
    this.loadAvailableProjects();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  loadBonusRequests() {
    this.isLoading = true;
    console.log('🔄 [ManageBonuses] Iniciando carga de bonificaciones...');
    
    // Cargar todas las bonificaciones de todos los usuarios y filtrar las que tienen requestStatus
    const subscription = this.afs.collection('user-bonuses')
      .snapshotChanges()
      .subscribe({
        next: async (snapshots) => {
          try {
            console.log('📦 [ManageBonuses] Recibidos', snapshots.length, 'documentos de user-bonuses');
            const allRequests: BonusRequest[] = [];
            
            // Paso 1: Recopilar todos los userIds únicos y datos de bonificaciones
            const userIds = new Set<string>();
            const userBonusesMap = new Map<string, { bonuses: any[], snapshot: any }>();
            
            for (const snapshot of snapshots) {
              const userBonusDoc = snapshot.payload.doc.data() as any;
              const userId = snapshot.payload.doc.id;
              const bonuses = userBonusDoc.bonuses || [];
              
              userIds.add(userId);
              userBonusesMap.set(userId, { bonuses, snapshot });
            }
            
            // Paso 2: Cargar todos los HubSpot IDs en paralelo (usando cache cuando sea posible)
            const userIdsToFetch = Array.from(userIds).filter(userId => !this.hubspotIdCache.has(userId));
            console.log('🔄 [ManageBonuses] Cargando HubSpot IDs para', userIdsToFetch.length, 'usuarios nuevos (cache:', this.hubspotIdCache.size, 'usuarios)');
            
            const hubspotIdPromises = userIdsToFetch.map(async (userId) => {
              try {
                const userDoc = await firstValueFrom(this.afs.collection('users').doc(userId).get());
                if (userDoc && userDoc.exists) {
                  const userData = userDoc.data() as any;
                  const hubspotId = userData.hubspotId ? userData.hubspotId.toString() : 'N/A';
                  // Guardar en cache
                  this.hubspotIdCache.set(userId, hubspotId);
                  return { userId, hubspotId };
                }
                this.hubspotIdCache.set(userId, 'N/A');
                return { userId, hubspotId: 'N/A' };
              } catch (error) {
                console.error('Error obteniendo HubSpot ID para usuario', userId, ':', error);
                this.hubspotIdCache.set(userId, 'N/A');
                return { userId, hubspotId: 'N/A' };
              }
            });
            
            // Esperar a que todas las consultas se completen en paralelo
            const hubspotIds = await Promise.all(hubspotIdPromises);
            const hubspotIdMap = new Map<string, string>(this.hubspotIdCache); // Empezar con el cache
            hubspotIds.forEach(({ userId, hubspotId }) => {
              hubspotIdMap.set(userId, hubspotId);
            });
            
            console.log('✅ [ManageBonuses] HubSpot IDs cargados para', hubspotIdMap.size, 'usuarios (nuevos:', userIdsToFetch.length, ', desde cache:', Array.from(userIds).filter(id => this.hubspotIdCache.has(id)).length, ')');
            
            // Paso 3: Procesar las bonificaciones usando el mapa de HubSpot IDs
            for (const [userId, { bonuses }] of userBonusesMap) {
              const userHubspotId = hubspotIdMap.get(userId) || 'N/A';
              
              bonuses.forEach((bonus: any) => {
                if (bonus.requestStatus) {
                  const finalHubspotId = bonus.hubspotId || userHubspotId;
                  
                  // Para solicitudes existentes sin los nuevos campos, usar valores por defecto
                  const processedBonus = {
                    ...bonus,
                    userId: userId,
                    hubspotId: finalHubspotId,
                    // Valores por defecto para solicitudes existentes
                    approvalType: bonus.approvalType || (bonus.requestStatus === 'APPROVED' ? 'both' : undefined),
                    // Para solicitudes aprobadas existentes, usar los valores originales si no hay campos específicos
                    approvedAmount: bonus.approvedAmount !== undefined ? bonus.approvedAmount : (bonus.requestStatus === 'APPROVED' && bonus.amount > 0 ? bonus.amount : undefined),
                    approvedPercentage: bonus.approvedPercentage !== undefined ? bonus.approvedPercentage : (bonus.requestStatus === 'APPROVED' && bonus.percentage > 0 ? bonus.percentage : undefined),
                    appliedAmount: bonus.appliedAmount !== undefined ? bonus.appliedAmount : (bonus.requestStatus === 'APPROVED' && bonus.amount > 0 ? bonus.amount : undefined),
                    appliedPercentage: bonus.appliedPercentage !== undefined ? bonus.appliedPercentage : (bonus.requestStatus === 'APPROVED' && bonus.percentage > 0 ? bonus.percentage : undefined)
                  };
                  
                  allRequests.push(processedBonus);
                }
              });
            }
            
            console.log('✅ [ManageBonuses] Procesadas', allRequests.length, 'solicitudes de bonificaciones');
            this.bonusRequests = allRequests;
            this.isLoading = false;
            console.log('🔄 [ManageBonuses] isLoading actualizado a:', this.isLoading, 'bonusRequests.length:', this.bonusRequests.length);
            this.cdr.markForCheck(); // Marcar para detección de cambios
            console.log('✅ [ManageBonuses] Marca para detección de cambios aplicada');
          } catch (error) {
            console.error('❌ [ManageBonuses] Error en el procesamiento:', error);
            this.isLoading = false;
            this.bonusRequests = [];
            this.cdr.markForCheck(); // Marcar para detección de cambios
          }
        },
        error: (error) => {
          console.error('❌ [ManageBonuses] Error en la suscripción:', error);
          this.isLoading = false;
          this.bonusRequests = [];
          this.cdr.markForCheck(); // Marcar para detección de cambios
          Swal.fire({
            title: 'Error al cargar bonificaciones',
            text: 'No se pudieron cargar las solicitudes de bonificaciones. Por favor, intenta de nuevo.',
            icon: 'error',
            confirmButtonText: 'Aceptar'
          });
        }
      });

    this.subscriptions.push(subscription);
    
    // Timeout de seguridad: si después de 30 segundos no se ha resuelto, desactivar loading
    setTimeout(() => {
      if (this.isLoading) {
        console.warn('⚠️ [ManageBonuses] Timeout: La carga está tomando demasiado tiempo');
        this.isLoading = false;
        this.cdr.markForCheck(); // Marcar para detección de cambios
        if (this.bonusRequests.length === 0) {
          Swal.fire({
            title: 'Tiempo de espera agotado',
            text: 'La carga de bonificaciones está tomando más tiempo del esperado. Por favor, intenta actualizar.',
            icon: 'warning',
            confirmButtonText: 'Aceptar'
          });
        }
      }
    }, 30000);
  }

  loadAvailableProjects() {
    const subscription = this.afs.collection('investments')
      .valueChanges()
      .subscribe({
        next: (projects: any[]) => {
          this.availableProjects = projects || [];
        },
        error: (error) => {
          console.error('Error loading projects:', error);
        }
      });

    this.subscriptions.push(subscription);
  }

  getPendingRequests(): BonusRequest[] {
    return this.bonusRequests.filter(request => request.requestStatus === 'PENDING');
  }

  getApprovedRequests(): BonusRequest[] {
    return this.bonusRequests.filter(request => request.requestStatus === 'APPROVED');
  }

  getRejectedRequests(): BonusRequest[] {
    return this.bonusRequests.filter(request => request.requestStatus === 'REJECTED');
  }

  getProjectName(projectId: string): string {
    const project = this.availableProjects.find(p => p.id === projectId);
    return project?.nameTokens || project?.title || project?.location || 'Proyecto no encontrado';
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'APPROVED':
        return 'Aprobada';
      case 'REJECTED':
        return 'Rechazada';
      default:
        return status;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'PENDING':
        return 'status-pending';
      case 'APPROVED':
        return 'status-approved';
      case 'REJECTED':
        return 'status-rejected';
      default:
        return 'status-default';
    }
  }

  getPaymentMethodText(method: string): string {
    switch (method) {
      case 'blockchain':
        return 'Blockchain';
      case 'transferencia':
        return 'Transferencia';
      case 'tarjeta':
        return 'Tarjeta';
      case 'manual':
        return 'Manual';
      default:
        return method;
    }
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatAmount(amount: number): string {
    return `${amount.toFixed(2)}€`;
  }

  formatPercentage(percentage: number): string {
    return `${percentage}%`;
  }

  getApprovalText(approvalType: 'amount' | 'percentage' | 'both'): string {
    switch (approvalType) {
      case 'amount':
        return 'Importe';
      case 'percentage':
        return 'Porcentaje';
      case 'both':
        return 'Bonificación Completa';
      default:
        return 'Bonificación';
    }
  }

  getApprovalDetails(request: BonusRequest, approvalType: 'amount' | 'percentage' | 'both'): string {
    let details = '';
    
    if (approvalType === 'amount' || approvalType === 'both') {
      if (request.amount > 0) {
        details += `<p><strong>Importe:</strong> ${this.formatAmount(request.amount)}</p>`;
      }
    }
    
    if (approvalType === 'percentage' || approvalType === 'both') {
      if (request.percentage > 0) {
        details += `<p><strong>Porcentaje:</strong> ${this.formatPercentage(request.percentage)}</p>`;
      }
    }
    
    return details;
  }

  getApprovalTypeText(approvalType: string): string {
    switch (approvalType) {
      case 'amount': return 'Solo Importe';
      case 'percentage': return 'Solo Porcentaje';
      case 'both': return 'Completa';
      default: return 'Desconocido';
    }
  }

  getApprovalTypeClass(approvalType: string): string {
    switch (approvalType) {
      case 'amount': return 'approval-amount';
      case 'percentage': return 'approval-percentage';
      case 'both': return 'approval-both';
      default: return 'approval-unknown';
    }
  }

  async approveBonus(request: BonusRequest, approvalType: 'amount' | 'percentage' | 'both' = 'both') {
    try {
      const approvalText = this.getApprovalText(approvalType);
      const approvalDetails = this.getApprovalDetails(request, approvalType);
      
      const { isConfirmed } = await Swal.fire({
        title: `Aprobar ${approvalText}`,
        html: `
          <div class="bonus-modal-info">
            <p><strong>Usuario:</strong> ${request.userEmail}</p>
            <p><strong>Proyecto:</strong> ${request.projectName}</p>
            ${approvalDetails}
          </div>
          <p class="bonus-modal-note">
            <i class="bi bi-info-circle"></i> La bonificación se aplicará automáticamente al proyecto seleccionado por el usuario.
          </p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: `Aprobar ${approvalText}`,
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d'
      });

      if (isConfirmed) {
        // Actualizar el estado con el tipo de aprobación
        await this.updateBonusStatusWithApprovalType(request.requestId!, 'APPROVED', approvalType);
        
        // Aplicar la bonificación al usuario
        await this.applyBonusToUser(request, approvalType);
        
        Swal.fire({
          title: '¡Bonificación aprobada!',
          text: `Se ha aprobado: ${approvalText}`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error approving bonus:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ha ocurrido un error al aprobar la bonificación',
        icon: 'error'
      });
    }
  }

  async rejectBonus(request: BonusRequest) {
    try {
      const { value: rejectionReason } = await Swal.fire({
        title: 'Rechazar Bonificación',
        html: `
          <div class="bonus-modal-info">
            <p><strong>Usuario:</strong> ${request.userEmail}</p>
            <p><strong>Proyecto:</strong> ${request.projectName}</p>
            <p><strong>Importe:</strong> ${this.formatAmount(request.amount)}</p>
            <p><strong>Porcentaje:</strong> ${this.formatPercentage(request.percentage)}</p>
          </div>
          <label for="rejectionReason" class="bonus-modal-label">
            Motivo del rechazo:
          </label>
          <textarea id="rejectionReason" class="swal2-textarea bonus-modal-textarea" placeholder="Introduce el motivo del rechazo..."></textarea>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Rechazar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        preConfirm: () => {
          const reasonTextarea = document.getElementById('rejectionReason') as HTMLTextAreaElement;
          const reason = reasonTextarea.value.trim();
          
          if (!reason) {
            Swal.showValidationMessage('Debes introducir un motivo para el rechazo');
            return false;
          }
          
          return reason;
        }
      });

      if (rejectionReason) {
        await this.updateBonusStatus(request.requestId!, 'REJECTED', rejectionReason);
        
        Swal.fire({
          title: 'Bonificación rechazada',
          text: 'La bonificación ha sido rechazada',
          icon: 'info',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error rejecting bonus:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ha ocurrido un error al rechazar la bonificación',
        icon: 'error'
      });
    }
  }

  async reapproveBonus(request: BonusRequest) {
    try {
      // Buscar el proyecto original de la bonificación
      let originalProjectId = request.projectId;
      
      // Si no hay projectId, intentar encontrarlo por nombre
      if (!originalProjectId) {
        const matchingProject = this.availableProjects.find(p => 
          (p.nameTokens === request.projectName || 
           p.title === request.projectName ||
           p.location === request.projectName)
        );
        originalProjectId = matchingProject?.id;
      }
      
      // Si aún no encontramos el proyecto, usar el primero disponible (fallback)
      if (!originalProjectId && this.availableProjects.length > 0) {
        originalProjectId = this.availableProjects[0].id;
      }
      
      const { isConfirmed } = await Swal.fire({
        title: 'Reaprobar Bonificación',
        html: `
          <div class="bonus-modal-info">
            <p><strong>Usuario:</strong> ${request.userEmail}</p>
            <p><strong>Proyecto original:</strong> ${request.projectName}</p>
            <p><strong>Importe:</strong> ${this.formatAmount(request.amount)}</p>
            <p><strong>Porcentaje:</strong> ${this.formatPercentage(request.percentage)}</p>
            <p><strong>Motivo anterior del rechazo:</strong> ${request.rejectionReason || 'No especificado'}</p>
          </div>
          <p class="bonus-modal-note">
            <i class="bi bi-info-circle"></i> La bonificación se aplicará al proyecto original: <strong>${request.projectName}</strong>
          </p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Reaprobar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#6c757d'
      });

      if (isConfirmed) {
        await this.updateBonusStatus(request.requestId!, 'APPROVED');
        
        // Aplicar la bonificación al usuario (usando el proyecto original)
        await this.applyBonusToUser(request);
        
        Swal.fire({
          title: '¡Bonificación reaprobada!',
          text: `La bonificación ha sido reaprobada y aplicada al proyecto: ${request.projectName}`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error reapproving bonus:', error);
      Swal.fire({
        title: 'Error',
        text: 'Ha ocurrido un error al reaprobar la bonificación',
        icon: 'error'
      });
    }
  }

  private async updateBonusStatus(requestId: string, status: 'APPROVED' | 'REJECTED', rejectionReason?: string) {
    // Buscar la bonificación en todos los usuarios
    const userDocs = await firstValueFrom(this.afs.collection('user-bonuses').get());
    
    if (!userDocs) return;
    
    for (const userDoc of userDocs.docs) {
      const userData = userDoc.data() as any;
      const bonuses = userData.bonuses || [];
      
      const bonusIndex = bonuses.findIndex((b: any) => b.requestId === requestId);
      
      if (bonusIndex !== -1) {
        // Actualizar la bonificación
        bonuses[bonusIndex].requestStatus = status;
        bonuses[bonusIndex].reviewedAt = Date.now();
        bonuses[bonusIndex].reviewedBy = 'admin';
        
        // La bonificación se aplica al proyecto ya seleccionado por el usuario
        // No necesitamos campos adicionales ya que projectId y projectName ya contienen esta información
        
        if (status === 'REJECTED' && rejectionReason) {
          bonuses[bonusIndex].rejectionReason = rejectionReason;
        }
        
        // Actualizar el documento del usuario
        await userDoc.ref.update({ bonuses });
        break;
      }
    }
  }

  private async updateBonusStatusWithApprovalType(requestId: string, status: 'APPROVED' | 'REJECTED', approvalType: 'amount' | 'percentage' | 'both', rejectionReason?: string) {
    // Buscar la bonificación en todos los usuarios
    const userDocs = await firstValueFrom(this.afs.collection('user-bonuses').get());
    
    if (!userDocs) return;
    
    for (const userDoc of userDocs.docs) {
      const userData = userDoc.data() as any;
      const bonuses = userData.bonuses || [];
      
      const bonusIndex = bonuses.findIndex((b: any) => b.requestId === requestId);
      
      if (bonusIndex !== -1) {
        // Actualizar la bonificación
        bonuses[bonusIndex].requestStatus = status;
        bonuses[bonusIndex].reviewedAt = Date.now();
        bonuses[bonusIndex].reviewedBy = 'admin';
        bonuses[bonusIndex].approvalType = approvalType;
        
        // Solo marcar como aprobado lo que se está aprobando
        if (approvalType === 'amount' || approvalType === 'both') {
          bonuses[bonusIndex].approvedAmount = bonuses[bonusIndex].amount;
        }
        if (approvalType === 'percentage' || approvalType === 'both') {
          bonuses[bonusIndex].approvedPercentage = bonuses[bonusIndex].percentage;
        }
        
        if (status === 'REJECTED' && rejectionReason) {
          bonuses[bonusIndex].rejectionReason = rejectionReason;
        }
        
        // Actualizar el documento del usuario
        await userDoc.ref.update({ bonuses });
        break;
      }
    }
  }

  private async applyBonusToUser(request: BonusRequest, approvalType?: 'amount' | 'percentage' | 'both') {
    try {
      // La bonificación se aplica al proyecto ya seleccionado por el usuario
      // No necesitamos buscar el proyecto porque ya está en request.projectName

      // Buscar y actualizar la bonificación existente
      const userBonusDocRef = this.afs.collection('user-bonuses').doc(request.userId);
      const userBonusDoc = await firstValueFrom(userBonusDocRef.get());
      
      if (userBonusDoc && userBonusDoc.exists) {
        const currentData = userBonusDoc.data() as any;
        const currentBonuses = currentData.bonuses || [];
        
        // Encontrar la bonificación por requestId
        const bonusIndex = currentBonuses.findIndex((b: any) => b.requestId === request.requestId);
        
        if (bonusIndex !== -1) {
          // Actualizar la bonificación existente
          currentBonuses[bonusIndex].status = 'APPLIED';
          currentBonuses[bonusIndex].requestStatus = 'APPROVED';
          currentBonuses[bonusIndex].appliedAt = request.projectName;
          currentBonuses[bonusIndex].reviewedAt = Date.now();
          currentBonuses[bonusIndex].reviewedBy = 'admin';
          
          // Agregar información del tipo de aprobación
          if (approvalType) {
            currentBonuses[bonusIndex].approvalType = approvalType;
            
            // Solo marcar como aplicado lo que se aprobó
            if (approvalType === 'amount' || approvalType === 'both') {
              currentBonuses[bonusIndex].appliedAmount = request.amount;
            }
            if (approvalType === 'percentage' || approvalType === 'both') {
              currentBonuses[bonusIndex].appliedPercentage = request.percentage;
            }
          }
          
          await userBonusDocRef.update({
            bonuses: currentBonuses
          });
        }
      }
    } catch (error) {
      console.error('Error applying bonus to user:', error);
      throw error;
    }
  }

  refreshRequests() {
    this.loadBonusRequests();
  }

  // Métodos para fold/unfold
  togglePendingSection() {
    this.isPendingExpanded = !this.isPendingExpanded;
  }

  toggleApprovedSection() {
    this.isApprovedExpanded = !this.isApprovedExpanded;
  }

  toggleRejectedSection() {
    this.isRejectedExpanded = !this.isRejectedExpanded;
  }

  // Método para expandir todas las secciones
  expandAllSections() {
    this.isPendingExpanded = true;
    this.isApprovedExpanded = true;
    this.isRejectedExpanded = true;
  }

  // Método para colapsar todas las secciones
  collapseAllSections() {
    this.isPendingExpanded = false;
    this.isApprovedExpanded = false;
    this.isRejectedExpanded = false;
  }

  // Método para descargar Excel de bonificaciones
  downloadExcel() {
    try {
      // Preparar datos para Excel
      const excelData = this.bonusRequests.map(request => ({
        'ID': request.id,
        'Usuario': request.userEmail,
        'Proyecto': request.projectName,
        'Importe': `${request.amount}€`,
        'Porcentaje': `${request.percentage}%`,
        'Estado': this.getStatusText(request.requestStatus || 'PENDING'),
        'Fecha Solicitud': this.formatDate(request.configuredAt),
        'Fecha Revisión': request.reviewedAt ? this.formatDate(request.reviewedAt) : 'Pendiente',
        'Revisado Por': request.reviewedBy || 'Pendiente',
        'Método Pago': request.paymentMethod,
        'Aplicado En': request.appliedAt || 'No aplicado'
      }));

      // Crear libro de trabajo
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Bonificaciones');

      // Generar nombre de archivo con fecha actual
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const fileName = `bonificaciones_${dateStr}.xlsx`;

      // Descargar archivo
      XLSX.writeFile(workbook, fileName);

      // Mostrar mensaje de éxito
      Swal.fire({
        title: '¡Excel generado!',
        text: `Se ha descargado el archivo: ${fileName}`,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false
      });

    } catch (error) {
      console.error('Error generando Excel:', error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudo generar el archivo Excel',
        icon: 'error'
      });
    }
  }

}
